// Import 3rd-party dependencies
import { Component, OnDestroy, EventEmitter, Output, ViewChild, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { combineLatest, of } from 'rxjs';
import { switchMap, map, catchError, tap } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import { TeamsMsal2Provider, ProviderState } from '@microsoft/mgt';

// Import project-specific dependencies
import { NotificationsService } from '@app/services/notifications/notification.service';
import { TOP_NAV_COMPONENT_CONSTS } from '@app/config/constants';
import { ViewUserProfileComponent } from '@app/hub/view-user-profile/view-user-profile.component';
import { PeopleService } from '@app/services/people/people.service';
import { DdUser, UserProfileService } from '@app/services/user-profile/user-profile.service';
import { TeamsAuthService } from '@app/auth/teams/teams-auth.service';
import { environment } from '@app/environments/environment';
import { AnnouncementsComponent } from '../../admin/announcements/announcements.component';
import { AddPermissionService } from '@app/services/admin/add-permission/add-permission.service';

@Component({
  selector: 'app-top-navigation',
  templateUrl: './top-navigation.component.html',
  styleUrls: ['./top-navigation.component.css']
})
export class TopNavigationComponent implements OnDestroy {
  // Class attributes
  @Output() viewFav: EventEmitter<string>;
  @Output() notificationToggle: EventEmitter<string>;
  @ViewChild('menuTrigger') menuTrigger: MatMenuTrigger;
  profileMenuTimeout: NodeJS.Timeout;
  consts = TOP_NAV_COMPONENT_CONSTS;

  private msalService: MsalService;
  private teamsAuthService: TeamsAuthService;

  environmentType$ = environment.production;

  refreshNoifList() {
    this.notificationsService.refreshNoifList();
  }

  notifCount$ = this.notificationsService
    .filterOutDisabledItems(this.notificationsService.allNotifs$)
    .pipe(map((notifications) => notifications.reduce((total, notif) => total + notif.count, 0)));

  preferredName$ = this.peopleService.currentUser$.pipe(
    switchMap((user) =>
      combineLatest([
        of(user),
        this.profileService
          .getDdProfile(user.employeeId)
          .pipe(catchError(() => of({ core_id: user.employeeId } as DdUser)))
      ]).pipe(
        map(([graphUser, ddUser]) => {
          const graphFirstName = graphUser?.givenName;
          const ddFirstName = ddUser?.preferred_first_name || ddUser?.first_name;

          return (ddFirstName || graphFirstName || '').replace(
            this.consts.NAME_REGEX,
            (name: string, first: string, rest: string) => {
              return first.toUpperCase() + rest.toLowerCase();
            }
          );
        })
      )
    )
  );

  userPermission: any;
  ngOnInit(): void {
    this.peopleService.currentUser$
      .pipe(
        switchMap((user) => this.addPermissionService.searchPeople(user.mail)),
        map((permission) => {
          if (
            permission &&
            permission.length &&
            (permission[0].permissionLevels.directReports ||
              permission[0].permissionLevels.allDXC ||
              permission[0].permissionLevels.region?.length ||
              permission[0].permissionLevels.department?.length)
          )
            return permission[0];
          else return null;
        })
      )
      .subscribe((permission) => (this.userPermission = permission));
  }

  constructor(
    private router: Router,
    private notificationsService: NotificationsService,
    private peopleService: PeopleService,
    private profileService: UserProfileService,
    private addPermissionService: AddPermissionService,
    private injector: Injector,
    public dialog: MatDialog
  ) {
    this.viewFav = new EventEmitter<string>();
    this.notificationToggle = new EventEmitter<string>();
  }

  ngOnDestroy(): void {
    if (this.profileMenuTimeout) {
      clearTimeout(this.profileMenuTimeout);
    }
  }

  // Open user profile dialog
  showUserProfile(): void {
    this.dialog.open(ViewUserProfileComponent, {
      autoFocus: false,
      panelClass: 'profile-dialog-container',
      height: this.consts.PROFILE_DIALOG_HEIGHT
    });
  }

  // Open announcements dialog
  openAnnouncementsDialog(): void {
    this.dialog.open(AnnouncementsComponent, {
      data: { userPermission: this.userPermission },
      autoFocus: false,
      panelClass: 'announcements-dialog-container'
    });
  }

  // open profile dropdown
  openProfileMenu(): void {
    if (this.profileMenuTimeout) {
      clearTimeout(this.profileMenuTimeout);
    }
    this.menuTrigger.openMenu();
  }

  // close profile dropdown
  closeProfileMenu(): void {
    this.profileMenuTimeout = setTimeout(() => {
      this.menuTrigger.closeMenu();
    }, this.consts.TIMEOUT);
  }

  // navigate to hub page
  navigateToHomePage(): void {
    this.scrollToPageContent(this.consts.PAGE_TOP_OFFSET_POSITION);
    document.getElementById('DXCHubButton').click();
    this.router.navigate['/'];
  }

  // scroll the page content
  scrollToPageContent(offsetPosition: number): void {
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }

  openFav(): void {
    this.viewFav.emit();
  }

  // notifications sidenav bar
  toggleNotifications(): void {
    this.notificationToggle.emit();
  }

  // open the link in new tab
  openLink(url: string): void {
    window.open(url, '_blank');
  }

  // logout from DXC Hub
  async logout(): Promise<void> {
    if (TeamsMsal2Provider.isAvailable) {
      this.teamsAuthService = this.injector.get(TeamsAuthService);

      this.teamsAuthService.getGraphToolkitProvider().setState(ProviderState.SignedOut);
      await this.teamsAuthService.getGraphToolkitProvider().logout();
    } else {
      this.msalService = this.injector.get(MsalService);
      this.msalService.logoutRedirect();
    }
  }
}
